import React, { useState, useEffect, useCallback } from 'react';
import {
  Row,
  Form,
  FormGroup,
  Spinner,
  Label,
  Col,
  Input,
  Button,
} from 'reactstrap';
import isEmpty from 'lodash/isEmpty';
import debounce from 'lodash/debounce';
import moment from 'moment';
import { useMutation, useQueryClient } from 'react-query';
import { exportExcel } from 'api/crud';
import { useDispatch, useSelector } from 'react-redux';
import { getSearchColumns } from 'utils/tools';
import { useAuth } from 'hooks/useAuth';
import { useRole } from 'hooks/useRole';
import { getCounties, getSubCounties, getWards } from 'store/location/actions';
import { DateField, SelectField } from 'components/Common/fields';

const dateFormat = 'YYYY-MM-DD';

const MainFilter = ({
  onSearch = () => { },
  showDateFilter = false,
  showTransFilter = false,
  hasExport = false,
  pageSize,
  pagination,
  resource,
  fromDate = moment().subtract(90, 'days').format(dateFormat),
  toDate = moment().format(dateFormat),
  onChangePagination = () => { },
  inlineActions = false,
}) => {
  const dispatch = useDispatch();
  const [exporting, setExporting] = useState(false);

  const { isAuthenticated } = useAuth();
  const {
    isFacilitator,
    isSiteCord,
    isCountyCoordinator,
    isClusterLeader,
    isRegionalCountyCoordinator,
    addresses,
  } = useRole();
  const address = addresses[0];

  const { wardId, constituencyId, countyId, regionId } = isAuthenticated
    ? address
    : {};

  const { claimSummary, counties, constituencies, regions, wards } =
    useSelector((state) => ({
      claimSummary: state.reports.claims,
      counties: state.location.counties,
      constituencies: state.location.constituencies,
      regions: state.location.regions,
      wards: state.location.wards,
    }));

  const [addressData, setAddressData] = useState({
    fromDate: moment(fromDate).format(dateFormat),
    toDate,
  });

  const [transactionCode, setTransactionCode] = useState(null);
  const [idNumber, setIdNumber] = useState(null);
  const makeDebounceSearch = useCallback(
    debounce((value, key) => {

      if (key && value) {
        onChangePagination(
          { limit: pagination.pageSize, page: pagination.pageNumber },
          {
            [key]: value,
          },
        );
      } else if (key && !value) {
        onChangePagination(
          { limit: pagination.pageSize, page: pagination.pageNumber },
          {},
        );
      }
    }, 600),
    [pagination],
  );

  const onChangeRegion = (e) => {
    const regionId = e.target.value;
    makeDebounceSearch([regionId], 'regionIds');
    setAddressData({
      ...addressData,
      regionId,
      countryId: '',
      countyId: null,
      constituencyId: '',
      wardId: '',
    });
    dispatch(
      getCounties({
        ids: [regionId],
        filter: isClusterLeader || isCountyCoordinator || isSiteCord || isFacilitator,
        addresses,
      }),
    );
  };

  const onChangeCounty = (e) => {
    const countyId = e.target.value;
    makeDebounceSearch([countyId], 'countyIds');
    setAddressData({
      ...addressData,
      countyId,
      constituencyId: '',
      wardId: '',
    });
    dispatch(
      getSubCounties({
        ids: [countyId],
        filter: isSiteCord,
        addresses,
      }),
    );
  };

  const onChangeSubCounty = (e) => {
    const constituencyId = e.target.value;
    makeDebounceSearch([constituencyId], 'constituencyIds');
    setAddressData({ ...addressData, constituencyId, wardId: '' });
    dispatch(
      getWards({
        ids: [constituencyId],
        filter: isFacilitator,
        addresses,
      }),
    );
  };

  const onChangeWard = (e) => {
    const wardId = e.target.value;
    makeDebounceSearch([wardId], 'wardIds');
    setAddressData({ ...addressData, wardId });
  };

  const handleReset = () => {
    setAddressData({
      fromDate: moment().startOf('month').format(dateFormat),
      toDate: moment().format(dateFormat),
    });
  };

  const handleChange = (e) => {
    setAddressData({ ...addressData, [e.target.name]: e.target.value });
  };

  const handleExport = async () => {
    try {
      const searchColumns = pagination?.searchColumns.map((column) => {
        if (column.name === 'startDate') {
          return {
            ...column,
            value: moment(addressData.fromDate).format('DD-MM-YYYY'),
          };
        }
        if (column.name === 'endDate') {
          return {
            ...column,
            value: moment(addressData.toDate).format('DD-MM-YYYY'),
          };
        }
        return column;
      });
      let data = {
        ...pagination,
        pageNumber: pagination.pageNumber - 1,
        searchColumns: [
          ...searchColumns,
          ...(addressData.constituencyId
            ? [
              {
                name: 'constituencyIds',
                values: [addressData.constituencyId],
              },
            ]
            : []),
          ...(addressData.wardId
            ? [{ name: 'wardIds', values: [addressData.wardId] }]
            : []),

          ...(addressData.countyId
            ? [{ name: 'countyIds', values: [addressData.countyId] }]
            : []),
          ...(addressData.regionId
            ? [{ name: 'regionIds', values: [addressData.regionId] }]
            : []),
        ],
      };
      console.log(data);

      await exportExcel(
        resource,
        data,
        (res) => {
          console.log(res); 
        },
        (errorMessage) => {
          console.log(errorMessage);
        },
        setExporting,
      );
    } catch (error) {
      console.log(error);
      setExporting(false);
    }
  };

  const handleSubmit = () => {
    const dateFormat = 'DD-MM-YYYY';

    let search = {
      ...(showDateFilter && {
        startDate: {
          name: 'startDate',
          value: moment(addressData.fromDate).format(dateFormat),
        },
        endDate: {
          name: 'endDate',
          value: moment(addressData.toDate).format(dateFormat),
        },
      }),

      ...(transactionCode && {
        transactionNumber: {
          name: 'transactionNumber',
          value: transactionCode,
        },
      }),
    };

    // 🔥 ROLE-BASED FALLBACKS
    if (isFacilitator) {
      search.wardId = {
        name: 'wardId',
        value: addressData.wardId || wardId,
      };
    }

    if (isSiteCord) {
      search.constituencyId = {
        name: 'constituencyId',
        value: addressData.constituencyId || constituencyId,
      };

      search.wardId = {
        name: 'wardId',
        value: addressData.wardId || wardId,
      };
    }

    if (isCountyCoordinator) {
      search.countyId = {
        name: 'countyId',
        value: addressData.countyId || countyId,
      };
    }

    if (isRegionalCountyCoordinator) {
      search.regionId = {
        name: 'regionId',
        value: addressData.regionId || regionId,
      };
    }

    // 👇 For admins or free users (manual selection)
    if (!isFacilitator && !isSiteCord && !isCountyCoordinator && !isRegionalCountyCoordinator) {
      if (addressData.regionId)
        search.regionId = { name: 'regionId', value: addressData.regionId };

      if (addressData.countyId)
        search.countyId = { name: 'countyId', value: addressData.countyId };

      if (addressData.constituencyId)
        search.constituencyId = { name: 'constituencyId', value: addressData.constituencyId };

      if (addressData.wardId)
        search.wardId = { name: 'wardId', value: addressData.wardId };
    }

    onSearch(search);
  };

  const onChangeTransactionCode = async (e) => {
    const value = e.target.value;
    setTransactionCode(value);
    makeDebounceSearch(value, 'transactionNumber');
  };

  const onChangeIdNumber = async (e) => {
    const value = e.target.value;
    setIdNumber(value);
    makeDebounceSearch(value, 'idNumber');
  };

  const getWardIdsValue = useCallback(() => {
    return getSearchColumns()?.find((t) => t.name === 'wardIds')?.value;
  }, []);

  const wardIds = getWardIdsValue();

  const actionButtons = (
    <>
      {hasExport && (
        <Button
          disabled={exporting}
          color="success"
          onClick={handleExport}
          className="d-inline-flex align-items-center"
        >
          {exporting ? (
            <Spinner size="sm" color="light" className="me-2" />
          ) : (
            <i className="bx bxs-file font-size-16 me-2" />
          )}
          Export
        </Button>
      )}
      <Button color="primary" onClick={handleSubmit} className="px-4">
        <i className="mdi mdi-magnify me-1" />
        Search
      </Button>
      <Button color="light" onClick={handleReset}>
        <i className="mdi mdi-refresh me-1" />
        Reset
      </Button>
    </>
  );

  return (
    <Form>
      {!inlineActions && (
      <Row className="mb-2">
        <Col md={24} className="d-flex justify-content-end">
          <Row>
            {hasExport && (
              <Col>
                <Button
                  disabled={exporting}
                  size="sm"
                  block
                  color="success"
                  onClick={handleExport}
                  className="d-flex align-items-center justify-content-between"
                >
                  <>
                    <span className="me-2">Export</span>
                    {exporting ? (
                      <Spinner size="sm" color="light" />
                    ) : (
                      <i className="bx bxs-file font-size-24"></i>
                    )}
                  </>
                </Button>
              </Col>
            )}
            <>
              <Col>
                <Button block color="light" onClick={handleReset}>
                  Reset
                </Button>
                &nbsp;&nbsp;
              </Col>
              <Col>
                <Button
                  block
                  color="primary"
                  onClick={handleSubmit}
                  className="pl-5 pr-5"
                >
                  Search
                </Button>
              </Col>
            </>
          </Row>
        </Col>
      </Row>
      )}
      <Row className={inlineActions ? 'align-items-end' : undefined}>
        {!isFacilitator &&
          !isSiteCord &&
          !isCountyCoordinator &&
          !isClusterLeader && (
            <Col xs="12" sm="6" lg>
              <FormGroup className="mb-3">
                <Label for="regionId">Select Region</Label>
                <SelectField
                  name="regionId"
                  id="regionId"
                  value={addressData?.regionId || ''}
                  onChange={onChangeRegion}
                >
                  <option defaultValue value={''}>
                    Select Region...
                  </option>
                  {regions &&
                    regions.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                </SelectField>
              </FormGroup>
            </Col>
          )}
        {!isFacilitator &&
          !isSiteCord &&
          !isCountyCoordinator &&
          !isClusterLeader && (
            <Col xs="12" sm="6" lg>
              <FormGroup className="mb-3">
                <Label for="countyId">Select County</Label>
                <SelectField
                  id="countyId"
                  name="countyId"
                  value={addressData?.countyId || ''}
                  onChange={onChangeCounty}
                >
                  <option defaultValue value={''}>
                    Select County...
                  </option>
                  {counties &&
                    counties.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                </SelectField>
              </FormGroup>
            </Col>
          )}
        {!isFacilitator && !isSiteCord && (
          <Col xs="12" sm="6" lg>
            <FormGroup className="mb-3">
              <Label for="constituencyId">Select Constituency</Label>
              <SelectField
                name="constituencyId"
                id="constituencyId"
                value={addressData?.constituencyId || ''}
                onChange={onChangeSubCounty}
              >
                <option defaultValue value={''}>
                  Select Constituency...
                </option>
                {constituencies?.length > 0 &&
                  (isFacilitator || isSiteCord || isClusterLeader
                    ? constituencies.filter((c) =>
                      addresses.map((a) => a.constituencyId).includes(c.id),
                    )
                    : constituencies
                  )?.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
              </SelectField>
            </FormGroup>
          </Col>
        )}
        {(!isFacilitator || wardIds?.length > 1) && (
          <Col xs="12" sm="6" lg>
            <FormGroup className="mb-3">
              <Label for="wardId">Select Ward</Label>
              <SelectField
                id="wardId"
                name="wardId"
                value={addressData?.wardId || ''}
                onChange={onChangeWard}
              >
                <option defaultValue value={''}>
                  Select Ward...
                </option>
                {(wardIds?.length > 0
                  ? wards?.filter((w) => wardIds?.includes(w.id))
                  : wards
                )?.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </SelectField>
            </FormGroup>
          </Col>
        )}
        {showDateFilter && (
          <>
            <Col xs="12" sm="6" lg>
              <FormGroup className="mb-3">
                <Label for={`fromDate`}>Start Date</Label>
                <DateField
                  name="fromDate"
                  id="fromDate"
                  placeholder="Select Start Date"
                  onChange={(e) => handleChange(e)}
                  value={addressData.fromDate}
                />
              </FormGroup>
            </Col>
            <Col xs="12" sm="6" lg>
              <FormGroup className="mb-3">
                <Label for={`toDate`}>End Date</Label>
                <DateField
                  name="toDate"
                  id="toDate"
                  placeholder="Select Start Date"
                  onChange={(e) => handleChange(e)}
                  value={addressData.toDate}
                />
              </FormGroup>
            </Col>
          </>
        )}
        {inlineActions && (
          <Col md="auto">
            <FormGroup className="mb-3 d-flex gap-2">{actionButtons}</FormGroup>
          </Col>
        )}
      </Row>
      {showTransFilter && (
        <Row>
          <Col md={5}>
            <FormGroup className="mb-3">
              <Label for={`transactionId`}>Transaction Code</Label>
              <Input
                name="transactionId"
                type="text"
                className="form-control"
                id="transactionId"
                placeholder="Enter Transaction Code"
                onChange={onChangeTransactionCode}
                value={transactionCode || ''}
              />
            </FormGroup>
          </Col>
          <Col md={5}>
            <FormGroup className="mb-3">
              <Label for={`transactionId`}>Account Number</Label>
              <Input
                name="idNumber"
                type="text"
                className="form-control"
                id="idNumber"
                placeholder="Enter Account Number"
                onChange={onChangeIdNumber}
                value={idNumber || ''}
              />
            </FormGroup>
          </Col>
        </Row>
      )}
    </Form>
  );
};

export default MainFilter;
